import { formatDate } from '@angular/common';
import { CmpsCardRowProgressData } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { CoachingInsightComponentAttributes } from '../../coaching-insight/oa/models/coaching.global.model';
import { I18nService } from '../../i18n/i18n.service';
import { epicDependencies } from '../../store';
import { MyCoachingCardGroup } from '../models/my-coaching-card-group.model';
import { StagingKpiThreshold } from '../models/staging-kpi-threshold.model';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { getProgressToolTipData } from './get-progress-tooltip-for-floa.function';

function calculateThresholdValue(value: number): string {
    return `${value.toFixed(0)}%`;
}

function calculateActualValue(
    score: number,
    thresholdOne: number,
    thresholdTwo: number,
): number {
    if (score < thresholdOne) return 0;
    if (score > thresholdTwo) return 100;
    return ((score - thresholdOne) / (thresholdTwo - thresholdOne)) * 100;
}

export function getProgressData(
    oaSummaryItem: MyCoachingCardGroup,
    coachingCardThreshold: StagingKpiThreshold[],
    componentAttributes?: CoachingInsightComponentAttributes,
    isTypeFloa?: boolean,
): CmpsCardRowProgressData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const userLocale =
        epicDependencies.injector.get<I18nService>(I18nService).locale;

    const baseTarget = oaSummaryItem.target * 100;
    const thresholdOne =
        coachingCardThreshold != null
            ? baseTarget * coachingCardThreshold[0].rangeEnd
            : 0;
    const thresholdTwo =
        coachingCardThreshold != null
            ? baseTarget * coachingCardThreshold[1].rangeEnd
            : 0;
    const score = oaSummaryItem.score * 100;

    const progressBarValues = [
        {
            thresholdValue: calculateThresholdValue(thresholdOne),
            actualValue: calculateThresholdValue(
                score > thresholdOne ? 100 : (score / thresholdOne) * 100,
            ),
            opportunityColor: oaSummaryItem.color,
        },
        {
            thresholdValue: calculateThresholdValue(
                thresholdTwo - thresholdOne,
            ),
            actualValue: calculateThresholdValue(
                calculateActualValue(score, thresholdOne, thresholdTwo),
            ),
            opportunityColor: oaSummaryItem.color,
        },
        {
            thresholdValue: calculateThresholdValue(100 - thresholdTwo),
            actualValue: calculateThresholdValue(
                score > thresholdTwo
                    ? ((score - thresholdTwo) / (100 - thresholdTwo)) * 100
                    : 0,
            ),
            opportunityColor: oaSummaryItem.color,
        },
    ];

    const progressTooltipData = [
        {
            label: translateService.instant(
                TranslationKeyMap._9920_COACHED_OPPORTUNITY,
            ),
            value: oaSummaryItem.coached.toString(),
        },
        {
            label: translateService.instant(
                TranslationKeyMap._9922_EXPECTED_OPPORTUNITY,
            ),
            value: oaSummaryItem.expected.toString(),
        },
        {
            label: translateService.instant(
                TranslationKeyMap._9921_UNCOACHED_OPPORTUNITY,
            ),
            value: oaSummaryItem.uncoached.toString(),
        },
        {
            label: translateService.instant(
                TranslationKeyMap._9923_LAST_UPDATED,
            ),
            value: formatDate(
                new Date(oaSummaryItem.lastUpdated),
                'MMMM d, YYYY, hh:mm a',
                userLocale,
            ),
        },
    ];

    return {
        progressBarValues,
        progressTooltipData:
            isTypeFloa != null
                ? getProgressToolTipData(componentAttributes)
                : progressTooltipData,
    };
}
